import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useState } from 'react'
import styles from './ToastContext.module.css'
import { Notification } from 'is24-corecss'

export const ToastContext = createContext<ToastContextProps>(null!)

export type Toast = {
  headline: string
  message: string | ReactElement
  type: 'success' | 'info' | 'warning' | 'error' | 'announcement' | 'neutral'
  timeout?: number
}

type ToastContextProps = { addToast: (toast: Toast) => void; closeToast: (toast: Toast) => void }

type Props = {
  children?: ReactNode
}

const DEFAULT_TIME_OUT = 4000

export const ToastContextProvider = ({ children }: Props) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const closeToast = useCallback(
    function (toast: Toast) {
      setToasts((toasts: Toast[]) => toasts.filter((t) => t !== toast))
    },
    [setToasts]
  )

  const addToast = useCallback(
    function (toast: Toast) {
      if (!toasts.map((t) => t.headline).includes(toast.headline)) {
        setToasts((toasts: Toast[]) => [...toasts, toast])
        setTimeout(() => closeToast(toast), toast.timeout || DEFAULT_TIME_OUT)
      }
    },
    [toasts, setToasts, closeToast]
  )

  return (
    <ToastContext.Provider value={{ addToast, closeToast }}>
      {toasts.length > 0 && (
        <div className={`${styles.toastsWrapper} margin-bottom-m`}>
          {toasts.map((toast) => (
            <Notification
              key={toast.headline}
              title={toast.headline}
              message={toast.message}
              type={toast.type}
              variant={'flat'}
              className='toast'
              onClose={() => closeToast(toast)}
            />
          ))}
        </div>
      )}
      {children}
    </ToastContext.Provider>
  )
}

export function useToastContext() {
  return useContext(ToastContext)
}
